import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { fetchPublicSiteContent } from "@/lib/adminApi";

/**
 * SiteContentProvider — fetches /api/site-content on mount and exposes the
 * merged result via context. Provides safe defaults if the network fails so
 * the site renders identical content with zero downtime.
 */

const CODE_DEFAULTS = {
    hero: {
        eyebrow: "Pranto.Gfx · Global Branding Studio",
        chip: "Since 2013",
        headline_lines: [
            "Building Brands That Look",
            "{gold:Premium}, Feel {italic:Strategic},",
            "And Stay Memorable.",
        ],
        description:
            "Pranto.Gfx creates high-end logo systems, brand identities, visual direction, and digital brand experiences for businesses ready to look more credible, more premium, and more unforgettable.",
        primary_cta: { label: "View Portfolio", style: "primary" },
        ghost_cta: { label: "Start A Project", style: "ghost" },
        rotating_words: [
            "Brand Identity",
            "Logo Systems",
            "Creative Direction",
            "Visual Strategy",
            "Premium Visual Design",
        ],
    },
    logos: {
        dark_theme_url: "",
        light_theme_url: "",
        alt_text: "Pranto.Gfx",
    },
    hero_rail_speed: "normal",
    stats: {
        items: [
            { label: "High-Value Brands", value: "350+", highlight: true },
            { label: "Projects Delivered", value: "700+", highlight: true },
            { label: "Client Revenue Impact", value: "$1B+", highlight: true },
        ],
    },
    about: {
        eyebrow: "About · The Studio",
        headline: "A decade of building brands that look {gold:unmistakably premium}.",
        founder_name: "Pranto",
        founder_chip_label: "Founder · Studio Lead",
        founder_role_chips: ["Creative Director", "Brand Identity Expert", "Brand Systems Architect"],
        image: "https://customer-assets.emergentagent.com/job_b090184f-8ffa-4acf-bd6b-d76aaaef9386/artifacts/lyw94g4s_asdasd.jpg",
        image_position: "50% 30%",
        paragraphs: [
            "With more than a decade of experience since 2013, Pranto has built 350+ high-value brands worldwide and delivered 700+ additional branding and visual projects across multiple industries.",
            "His work has contributed to over $1B in combined client revenue and includes collaborations with brands such as Nike, Hilton, Huawei, Alan Walker, Maserati, Lexus, Range Rover, Masafi, BEG Imports, Kingsman Tailors, FitX, The Luxe Co, and many more.",
            "Pranto specializes in strategic branding, visual identity systems, UI/UX direction, digital brand experiences, and high-impact creative systems designed to increase trust, positioning, memorability, and market authority.",
            "He currently oversees branding, visual direction, digital presentation, and creative execution for Pujols Roofing & Siding and Transcend Studios.",
        ],
        specialties: [
            "Strategic Branding",
            "Visual Identity Systems",
            "UI/UX Direction",
            "Digital Brand Experiences",
            "High-Impact Creative Systems",
        ],
    },
    work_history: {
        eyebrow: "Work History · Selected Collaborations",
        title_lines: ["A decade of work for", "{gold:brands that move markets}."],
        description:
            "From global icons to civic institutions, Pranto's creative direction has shaped identities trusted by clients across multiple continents.",
        collab_brands: [
            "Nike", "Hilton", "Huawei", "Alan Walker", "Maserati", "Lexus",
            "Range Rover", "Masafi", "BEG Imports", "Kingsman Tailors", "FitX",
            "The Luxe Co", "Bangladesh Navy", "Gazipur City Corporation",
        ].map((n, i) => ({ id: `cb-${i}`, name: n, order: i })),
        featured: [
            { id: "fh-1", brand: "Nike", scope: "Visual concept direction", year: "2019", order: 0 },
            { id: "fh-2", brand: "Huawei", scope: "Brand visual experience", year: "2018", order: 1 },
            { id: "fh-3", brand: "Alan Walker", scope: "Identity moment + visuals", year: "2020", order: 2 },
            { id: "fh-4", brand: "Bangladesh Navy", scope: "Visual systems direction", year: "2021", order: 3 },
            { id: "fh-5", brand: "Gazipur City Corporation", scope: "Civic branding direction", year: "2022", order: 4 },
            { id: "fh-6", brand: "Transcend Studios", scope: "Creative direction · ongoing", year: "Now", order: 5 },
            { id: "fh-7", brand: "Pujols Roofing & Siding", scope: "Brand & creative execution · ongoing", year: "Now", order: 6 },
        ],
        current_roles: [
            {
                id: "cr-1",
                brand: "Pujols Roofing & Siding",
                role: "Brand, Visual Direction & Creative Execution",
                sector: "Construction · Home Services",
                order: 0,
            },
            {
                id: "cr-2",
                brand: "Transcend Studios",
                role: "Creative Direction & Digital Presentation",
                sector: "Creative · Production",
                order: 1,
            },
        ],
    },
};

const SiteContentContext = createContext({ data: CODE_DEFAULTS, loaded: true });

export const SiteContentProvider = ({ children }) => {
    const [data, setData] = useState(CODE_DEFAULTS);
    const [loaded, setLoaded] = useState(false);

    useEffect(() => {
        let mounted = true;
        fetchPublicSiteContent()
            .then((res) => { if (mounted && res) setData(mergeDeep(CODE_DEFAULTS, res)); })
            .catch(() => { /* keep defaults */ })
            .finally(() => { if (mounted) setLoaded(true); });
        return () => { mounted = false; };
    }, []);

    const value = useMemo(() => ({ data, loaded }), [data, loaded]);
    return <SiteContentContext.Provider value={value}>{children}</SiteContentContext.Provider>;
};

export const useSiteContent = () => useContext(SiteContentContext);

const mergeDeep = (base, overlay) => {
    if (!overlay || typeof overlay !== "object") return base;
    const out = Array.isArray(base) ? [...base] : { ...base };
    for (const [k, v] of Object.entries(overlay)) {
        if (v && typeof v === "object" && !Array.isArray(v) && base && typeof base === "object" && !Array.isArray(base[k])) {
            out[k] = mergeDeep(base?.[k] ?? {}, v);
        } else {
            out[k] = v;
        }
    }
    return out;
};

export default SiteContentProvider;
